import { FC, ReactNode, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { AuthenticationPlatform } from 'src/contexts/AuthContext';
import { CustomerLoginProvider } from 'src/customerPages/context/login/CustomerLoginContext';
import CustomerLoginPage from 'src/customerPages/pages/auth/CustomerLoginPage';
import useAuth from 'src/hooks/useAuth';
import useQuery from 'src/hooks/useQuery';
import Login from 'src/pages/authentication/Login';
import NotFound from 'src/pages/NotFound';

export enum DesiredAuthPage {
  Internal = 'internal',
  External = 'external',
}
interface AuthGuardProps {
  authenticationPlatform: AuthenticationPlatform;
  children: ReactNode;
  desiredPage?: DesiredAuthPage;
}

const AuthGuard: FC<AuthGuardProps> = (props) => {
  const { children, authenticationPlatform, desiredPage = DesiredAuthPage.Internal } = props;
  const { isAuthenticated, platform: authenticatedPlatform } = useAuth();
  const location = useLocation();
  const [requestedLocation, setRequestedLocation] = useState(null);
  const [autoLogin] = useQuery<boolean>('login-redirect', false);

  if (!isAuthenticated) {
    if (location.pathname !== requestedLocation) setRequestedLocation(location.pathname);

    if (desiredPage === DesiredAuthPage.External) return (
      <CustomerLoginProvider>
        <CustomerLoginPage autoLogin={autoLogin} />
      </CustomerLoginProvider>
    );

    return <Login />;
  }

  if (authenticatedPlatform !== authenticationPlatform) {
    if (location.pathname === '/') return <Navigate to="/bank" />;

    return <NotFound />;
  }

  // This is done so that in case the route changes by any chance through other
  // means between the moment of request and the render we navigate to the initially
  // requested route.
  if (requestedLocation && location.pathname !== requestedLocation) {
    setRequestedLocation(null);

    return <Navigate to={requestedLocation} />;
  }

  return <>{children}</>;
};

export default AuthGuard;
